import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useUserStore } from '@/stores/userStore'
import * as userApi from '@/api/userApi'

/**
 * Store для управления правами на уровне пространств (уровень 2)
 * Хранит информацию о пространствах и проектах в них
 */
export const useSpaceStore = defineStore('space', () => {
  // === State ===
  const spaces = ref([]) // Массив пространств с вложенными проектами
  const isLoading = ref(false)
  const loadedAt = ref(null)
  const TTL = 5 * 60 * 1000 // 5 минут кэш

  // Кэшированный userStore
  let _userStore = null

  // === Helpers ===

  /**
   * Проверка готовности userStore
   */
  const isUserStoreReady = computed(() => _userStore && _userStore.token !== null)

  /**
   * Ожидание выполнения условия
   */
  async function waitForCondition(condition, timeout = 3000, interval = 100) {
    const start = Date.now()

    while (Date.now() - start < timeout) {
      if (condition()) return true
      await new Promise(resolve => setTimeout(resolve, interval))
    }

    throw new Error('Condition not met within timeout')
  }

  /**
   * Гарантирует инициализацию userStore
   */
  async function ensureUserStore() {
    if (!_userStore) {
      _userStore = useUserStore()
    }
    await waitForCondition(() => isUserStoreReady.value)
  }

  // === Getters ===

  /**
   * Получить пространство по ID
   */
  const getSpaceById = computed(() => (spaceId) => {
    return spaces.value.find(s => s.id === spaceId)
  })

  /**
   * Проверка владения пространством
   */
  const isSpaceOwner = computed(() => (spaceId) => {
    const space = spaces.value.find(s => s.id === spaceId)
    return space?.owner === true
  })

  const ownSpacesCount = computed(() => {
    return spaces.value.filter(s => s.owner === true).length
  })
  /**
   * Получить все проекты из всех пространств
   */
  const allProjects = computed(() => {
    return spaces.value.flatMap(space =>
      space.project?.map(project => ({
        ...project,
        spaceId: space.id,
        isSpaceOwner: space.owner
      })) || []
    )
  })

  /**
   * Получить проект по ID (из кэша spaces)
   */
  const getProjectById = computed(() => (projectId) => {
    for (const space of spaces.value) {
      const project = space.project?.find(p => p.id === projectId)
      if (project) {
        return {
          ...project,
          spaceId: space.id,
          isSpaceOwner: space.owner
        }
      }
    }
    return null
  })

  /**
   * Получить права проекта из space (краткая версия)
   */
  const getProjectPermissions = computed(() => (projectId) => {
    const project = getProjectById.value(projectId)
    return project?.['role-tarif']?.params || null
  })

  /**
   * Получить роль в проекте
   */
  const getProjectRole = computed(() => (projectId) => {
    const project = getProjectById.value(projectId)
    return project?.['role-tarif']?.role_title || null
  })

  /**
   * Получить тариф проекта
   */
  const getProjectTariff = computed(() => (projectId) => {
    const project = getProjectById.value(projectId)
    return project?.['role-tarif']?.tariff || null
  })

  /**
   * Проверка конкретного права проекта
   * @param {number} projectId - ID проекта
   * @param {string} permissionName - название права
   * @param {object} options - опции проверки
   * @param {boolean} options.extraCondition - дополнительное условие для 'limited'
   * @returns {boolean}
   */
  const hasProjectPermission = computed(() => (projectId, permissionName, options = {}) => {
    const project = getProjectById.value(projectId)
    if (!project) return false

    // Если owner пространства - все права доступны
    //if (project.isSpaceOwner === true) {
    //  return true
    //}

    const permissions = project['role-tarif']?.params
    if (!permissions) return false

    const permission = permissions[permissionName]
    if (!permission) return false

    // Если это объект с типом
    if (typeof permission === 'object' && 'value' in permission) {
      const { type, value } = permission

      if (type === 'boolean') {
        return value === true
      }

      if (type === 'enum') {
        if (value === 'full') return true
        if (value === 'none') return false
        if (value === 'limited') {
          // Для limited проверяем дополнительное условие
          return options.extraCondition === true
        }
      }

      // Для других типов возвращаем как boolean
      return Boolean(value)
    }

    // Если не объект - возвращаем как boolean
    return permission === true
  })

  /**
   * Получить значение права (raw value)
   */
  const getProjectPermissionValue = computed(() => (projectId, permissionName) => {
    const permissions = getProjectPermissions.value(projectId)
    if (!permissions) return null

    const permission = permissions[permissionName]
    if (!permission) return null

    // Возвращаем value если это объект
    if (typeof permission === 'object' && 'value' in permission) {
      return permission.value
    }

    return permission
  })

  /**
   * Проверка актуальности кэша
   */
  const isStale = computed(() => {
    if (!loadedAt.value) return true
    return Date.now() - loadedAt.value > TTL
  })

  // === Actions ===

  /**
   * Загрузить пространства с сервера
   */
  async function load() {
    isLoading.value = true
    try {
      // Ждем пока userStore будет готов
      await ensureUserStore()

      const data = await userApi.listSpacesPermissions(_userStore.token)
      setSpaces(data)
    } catch (error) {
      console.error('Failed to load spaces:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Установить пространства (внутренний метод)
   */
  function setSpaces(data) {
    spaces.value = Array.isArray(data) ? data : []
    loadedAt.value = Date.now()
  }

  /**
   * Частичное обновление пространств (мерж)
   * Обновляет только переданные пространства/проекты
   */
  function updatePartial(updates) {
    if (!Array.isArray(updates)) {
      console.warn('updatePartial expects an array of spaces')
      return
    }

    // Если данных еще нет - просто устанавливаем
    if (spaces.value.length === 0) {
      setSpaces(updates)
      return
    }

    // Создаем копию текущих данных
    const updated = [...spaces.value]

    // Мержим обновления
    updates.forEach(updateSpace => {
      const existingIndex = updated.findIndex(s => s.id === updateSpace.id)

      if (existingIndex >= 0) {
        // Пространство существует - мержим
        const existing = updated[existingIndex]

        // Мержим owner
        if (updateSpace.owner !== undefined) {
          existing.owner = updateSpace.owner
        }

        // Мержим проекты
        if (updateSpace.project && Array.isArray(updateSpace.project)) {
          existing.project = mergeProjects(
            existing.project || [],
            updateSpace.project
          )
        }

        updated[existingIndex] = existing
      } else {
        // Новое пространство - добавляем
        updated.push(updateSpace)
      }
    })

    spaces.value = updated
    loadedAt.value = Date.now()
  }

  /**
   * Обновить конкретное пространство
   */
  function updateSpace(spaceId, updates) {
    const index = spaces.value.findIndex(s => s.id === spaceId)
    if (index >= 0) {
      spaces.value[index] = {
        ...spaces.value[index],
        ...updates
      }
      loadedAt.value = Date.now()
    }
  }

  /**
   * Обновить конкретный проект в пространстве
   */
  function updateProject(spaceId, projectId, updates) {
    const space = spaces.value.find(s => s.id === spaceId)
    if (!space || !space.project) return

    const projectIndex = space.project.findIndex(p => p.id === projectId)
    if (projectIndex >= 0) {
      space.project[projectIndex] = deepMerge(
        space.project[projectIndex],
        updates
      )
      loadedAt.value = Date.now()
    }
  }

  /**
   * Очистка store
   */
  function clear() {
    spaces.value = []
    loadedAt.value = null
    isLoading.value = false
  }

  return {
    // State
    spaces,
    isLoading,
    loadedAt,

    // Getters
    getSpaceById,
    isSpaceOwner,
    ownSpacesCount,
    allProjects,
    getProjectById,
    getProjectPermissions,
    getProjectRole,
    getProjectTariff,
    hasProjectPermission,
    getProjectPermissionValue,
    isStale,

    // Actions
    load,
    setSpaces,
    updatePartial,
    updateSpace,
    updateProject,
    clear
  }
})

// === Helpers ===

/**
 * Мерж массивов проектов
 */
function mergeProjects(existing, updates) {
  const result = [...existing]

  updates.forEach(updateProject => {
    const existingIndex = result.findIndex(p => p.id === updateProject.id)

    if (existingIndex >= 0) {
      // Проект существует - мержим
      result[existingIndex] = deepMerge(result[existingIndex], updateProject)
    } else {
      // Новый проект - добавляем
      result.push(updateProject)
    }
  })

  return result
}

/**
 * Глубокое слияние объектов
 */
function deepMerge(target, source) {
  const output = { ...target }

  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      if (target[key] && typeof target[key] === 'object') {
        output[key] = deepMerge(target[key], source[key])
      } else {
        output[key] = source[key]
      }
    } else {
      output[key] = source[key]
    }
  }

  return output
}
